// client/src/api/assistantApi.ts

import { baseApi } from './baseApi';
import {
  ConversationAction,
  AssistantExecuteRequest,
  AssistantExecuteResponse,
  UpdateConversationActionRequest,
  // V2 types
  AssistantPlanRequest,
  AssistantPlanResponse,
  AssistantConfirmRequest,
  AssistantConfirmResponse,
  AssistantSkipRequest,
  AssistantSkipResponse,
} from '../types/api';

export const assistantApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Execute assistant to process a message and create actions
    executeAssistant: builder.mutation<AssistantExecuteResponse, AssistantExecuteRequest>({
      query: (body) => ({
        url: '/assistant/execute',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'ConversationAction', id: 'LIST' },
              { type: 'Task', id: 'LIST' },
              { type: 'Event', id: 'LIST' },
            ]
          : [],
    }),

    // Get all actions for a conversation
    getConversationActions: builder.query<ConversationAction[], string>({
      query: (conversationId) => `/assistant/conversations/${conversationId}/actions`,
      providesTags: (result, _error, conversationId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'ConversationAction' as const, id })),
              { type: 'ConversationAction', id: 'LIST' },
              { type: 'ConversationAction', id: `CONVERSATION_${conversationId}` },
            ]
          : [{ type: 'ConversationAction', id: 'LIST' }],
    }),

    // Update a conversation action (e.g., approve, edit payload, retry)
    updateConversationAction: builder.mutation<
      ConversationAction,
      { id: string; data: UpdateConversationActionRequest }
    >({
      query: ({ id, data }) => ({
        url: `/assistant/actions/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'ConversationAction', id },
        { type: 'ConversationAction', id: 'LIST' },
        { type: 'Task', id: 'LIST' },
        { type: 'Event', id: 'LIST' },
      ],
    }),

    // Retry a failed action
    retryConversationAction: builder.mutation<ConversationAction, string>({
      query: (id) => ({
        url: `/assistant/actions/${id}`,
        method: 'PATCH',
        body: { retry: true },
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'ConversationAction', id },
        { type: 'ConversationAction', id: 'LIST' },
        { type: 'Task', id: 'LIST' },
        { type: 'Event', id: 'LIST' },
      ],
    }),

    // Get pending actions for the current workspace
    getPendingActions: builder.query<ConversationAction[], void>({
      query: () => '/assistant/actions/pending',
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'ConversationAction' as const, id })),
              { type: 'ConversationAction', id: 'PENDING' },
            ]
          : [{ type: 'ConversationAction', id: 'PENDING' }],
    }),

    // ============================================================
    // Assistant V2 Endpoints
    // ============================================================

    // Plan actions for a message using LLM
    planAssistant: builder.mutation<AssistantPlanResponse, AssistantPlanRequest>({
      query: (body) => ({
        url: '/assistant/plan',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'ConversationAction', id: 'LIST' },
              { type: 'ConversationAction', id: 'PENDING' },
              ...(result.conversationId
                ? [{ type: 'ConversationAction' as const, id: `CONVERSATION_${result.conversationId}` }]
                : []),
            ]
          : [],
    }),

    // Confirm selected actions (move from pending_confirmation to queued)
    confirmActions: builder.mutation<AssistantConfirmResponse, AssistantConfirmRequest>({
      query: (body) => ({
        url: '/assistant/confirm',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'ConversationAction', id: 'LIST' },
              { type: 'ConversationAction', id: 'PENDING' },
              ...result.actions.map(({ id }) => ({ type: 'ConversationAction' as const, id })),
            ]
          : [],
    }),

    // Skip selected actions (move from pending_confirmation to skipped)
    skipActions: builder.mutation<AssistantSkipResponse, AssistantSkipRequest>({
      query: (body) => ({
        url: '/assistant/skip',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'ConversationAction', id: 'LIST' },
              { type: 'ConversationAction', id: 'PENDING' },
              ...result.actions.map(({ id }) => ({ type: 'ConversationAction' as const, id })),
            ]
          : [],
    }),

    // Get pending actions for a specific conversation (including pending_confirmation)
    getConversationPendingActions: builder.query<ConversationAction[], string>({
      query: (conversationId) => `/assistant/conversations/${conversationId}/pending`,
      providesTags: (result, _error, conversationId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'ConversationAction' as const, id })),
              { type: 'ConversationAction', id: 'PENDING' },
              { type: 'ConversationAction', id: `CONVERSATION_${conversationId}` },
            ]
          : [{ type: 'ConversationAction', id: 'PENDING' }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useExecuteAssistantMutation,
  useGetConversationActionsQuery,
  useUpdateConversationActionMutation,
  useRetryConversationActionMutation,
  useGetPendingActionsQuery,
  // V2 hooks
  usePlanAssistantMutation,
  useConfirmActionsMutation,
  useSkipActionsMutation,
  useGetConversationPendingActionsQuery,
} = assistantApi;
